import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const OrdersPanel = ({ restaurantId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();

    // --- REALTIME: Listen for NEW orders ---
    const channel = supabase
      .channel('orders-dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `restaurant_id=eq.${restaurantId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setOrders(prev => [payload.new, ...prev]);
            playDing();
          } else if (payload.eventType === 'UPDATE') {
            setOrders(prev => prev.map(o => o.id === payload.new.id ? payload.new : o));
          }
        })
      .subscribe();

    return () => supabase.removeChannel(channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    const { data } = await supabase
      .from('orders')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false }); // Newest first
    setOrders(data || []);
    setLoading(false);
  };

  const updateStatus = async (id, status, note = null) => {
    await supabase.from('orders').update({ status, status_note: note }).eq('id', id);
  };

  const playDing = () => {
    const audio = new Audio('https://codeskulptor-demos.commondatastorage.googleapis.com/pang/pop.mp3');
    audio.play().catch(e => console.log("Audio play failed", e));
  };

  if (loading) return <div className="p-10 text-center">Loading Orders...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">👨‍🍳 Live Kitchen Orders</h1>

      {orders.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl shadow-sm border">
          <div className="text-4xl mb-4">💤</div>
          <p className="text-gray-500 font-bold">No active orders right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {orders.map(order => (
            <div key={order.id} className={`bg-white rounded-2xl border-2 shadow-sm overflow-hidden flex flex-col ${order.status === 'Pending' ? 'border-yellow-400 ring-4 ring-yellow-100' : 'border-gray-100'}`}>

              {/* Header */}
              <div className={`p-4 flex justify-between items-start ${order.status === 'Pending' ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Table {order.table_number || 'N/A'}</h3>
                  <span className="text-xs text-gray-500">#{order.id} • {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${order.status === 'Pending' ? 'bg-yellow-500 text-white' :
                    order.status === 'Completed' ? 'bg-gray-200 text-gray-600' :
                      order.status === 'Rejected' ? 'bg-red-100 text-red-600' :
                        'bg-blue-100 text-blue-600'
                  }`}>
                  {order.status}
                </span>
              </div>

              {/* Items */}
              <div className="p-4 flex-1">
                {order.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center mb-2 border-b border-dashed border-gray-100 last:border-0 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">{item.quantity}x</span>
                      <span className="text-gray-700">{item.name}</span>
                    </div>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t flex justify-between font-bold text-gray-900">
                  <span>Total</span>
                  <span>₹{order.total_price}</span>
                </div>
              </div>

              {/* Actions */}
              {order.status !== 'Completed' && order.status !== 'Rejected' && (
                <div className="p-3 bg-gray-50 border-t flex flex-col gap-2">

                  {/* Step 1: Accept/Reject */}
                  {order.status === 'Pending' && (
                    <div className="flex gap-2">
                      <button onClick={() => updateStatus(order.id, 'Rejected')} className="flex-1 py-2 bg-white border border-red-200 text-red-600 rounded-lg font-bold text-xs hover:bg-red-50">Reject</button>
                      <button onClick={() => updateStatus(order.id, 'Accepted')} className="flex-1 py-2 bg-green-500 text-white rounded-lg font-bold text-xs hover:bg-green-600 shadow-md">Accept Order</button>
                    </div>
                  )}

                  {/* Step 2: Accept → Choose Status */}
                  {order.status === 'Accepted' && (
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => updateStatus(order.id, 'Cooking', 'Cooking now')} className="py-2 bg-orange-100 text-orange-700 rounded-lg font-bold text-xs hover:bg-orange-200">🔥 Cooking</button>
                      <button onClick={() => updateStatus(order.id, 'Cooking', 'Ready in 10 mins')} className="py-2 bg-blue-100 text-blue-700 rounded-lg font-bold text-xs hover:bg-blue-200">⏱️ 10 Mins</button>
                      <button onClick={() => updateStatus(order.id, 'Cooking', 'Ready in 5 mins')} className="py-2 bg-blue-100 text-blue-700 rounded-lg font-bold text-xs hover:bg-blue-200">⏱️ 5 Mins</button>
                      <button onClick={() => updateStatus(order.id, 'Ready', 'Ready to Serve!')} className="py-2 bg-green-600 text-white rounded-lg font-bold text-xs hover:bg-green-700 shadow-sm">✅ Ready</button>
                    </div>
                  )}

                  {/* Step 3: Cooking → Mark Ready */}
                  {order.status === 'Cooking' && (
                    <button onClick={() => updateStatus(order.id, 'Ready', 'Ready to Serve!')} className="w-full py-3 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700 shadow-sm flex items-center justify-center gap-2">
                      ✅ Mark Ready
                    </button>
                  )}

                  {/* Step 3: Complete */}
                  {order.status === 'Ready' && (
                    <button onClick={() => updateStatus(order.id, 'Completed')} className="w-full py-3 bg-gray-900 text-white rounded-lg font-bold text-sm hover:bg-black">
                      Mark Served / Paid
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPanel;